/* components/onboarding/EventsPreview.jsx ------------------------------
   For the presentation of QRProofs: an event as the owner posts it
   (facilityEvents/EventFormDialog.jsx, its texts showFacility.*) and as
   their customers then see it in the YummiTracker app. Example content,
   drawn so it is sharp and in the site language.
-------------------------------------------------------------------- */
import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const APP_GREEN = '#5FA915';
const VENUE = 'Le Bistrot du Village';

function Caption({ children }) {
  return (
    <Typography variant="overline" sx={{ fontWeight: 900, letterSpacing: 0.8, color: 'text.secondary', lineHeight: 1.4 }}>
      {children}
    </Typography>
  );
}

function Field({ label, value, focused }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: focused ? 'primary.main' : 'text.secondary' }}>{label}</Typography>
      <Typography
        variant="body2"
        sx={{ pb: 0.5, borderBottom: focused ? '2px solid' : '1px solid', borderColor: focused ? 'primary.main' : 'rgba(0,0,0,0.42)' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function EventsPreview() {
  const { t } = useTranslation();

  return (
    <Box
      aria-hidden="true"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#F4F6F9',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 1.25,
      }}
    >
      <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Caption>{t('onboarding.youPost')}</Caption>
        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Typography sx={{ fontWeight: 700 }}>{t('showFacility.newEvent')}</Typography>
          <Field label={t('showFacility.eventName')} value={t('onboarding.sampleName')} focused />
          <Field label={t('showFacility.message')} value={t('onboarding.sampleMessage')} />
          <Field label={t('showFacility.start')} value={t('onboarding.sampleWhen')} />
          <Box
            sx={{
              alignSelf: 'flex-end', px: 1.75, py: 0.75, borderRadius: 1, bgcolor: 'primary.main', color: 'primary.contrastText',
              fontSize: 13, fontWeight: 500, letterSpacing: 0.4, textTransform: 'uppercase',
            }}
          >
            {t('showFacility.save')}
          </Box>
        </Paper>
      </Box>

      <Box sx={{ alignSelf: 'center', color: 'primary.main', transform: { xs: 'rotate(90deg)', sm: 'none' }, lineHeight: 0 }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M4 12h15M13 6l6 6-6 6" />
        </svg>
      </Box>

      <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Caption>{t('onboarding.customersSee')}</Caption>
        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={APP_GREEN} strokeWidth="2">
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M3 10h18M8 3v4M16 3v4" />
            </svg>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('onboarding.aroundMe')}</Typography>
            <Box
              component="span"
              sx={{ ml: 'auto', fontSize: 11, fontWeight: 700, color: '#3F7A0C', bgcolor: '#EAF5DC', px: 0.875, py: 0.375, borderRadius: 999 }}
            >
              YummiTracker
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.25 }}>
            <Box sx={{ width: 4, borderRadius: 1, bgcolor: APP_GREEN, flex: '0 0 auto' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>{t('onboarding.sampleName')}</Typography>
              <Typography variant="body2" sx={{ color: '#555' }}>{t('onboarding.sampleWhen')}</Typography>
              <Typography variant="body2">{t('onboarding.sampleMessage')}</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>10 m · {VENUE}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.25, opacity: 0.55 }}>
            <Box sx={{ width: 4, borderRadius: 1, bgcolor: '#E8590C', flex: '0 0 auto' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('onboarding.sampleOffer')}</Typography>
              <Typography variant="caption" sx={{ color: '#555' }}>250 m · Café des Arts</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
